import {
	Chip,
	Tooltip,
	Typography,
	alpha,
	makeStyles,
} from "@material-ui/core";
import React from "react";
import {
	RECIPIENT_TYPE_COLOR,
	type RecipientType,
} from "../models/EmailAddressTopic";

const RECIPIENT_ORDER: RecipientType[] = ["TO", "CC", "BCC"];

export type RecipientValues = Partial<Record<RecipientType, string | null>>;

const useStyles = makeStyles(() => ({
	tooltip: {
		backgroundColor: "#fff",
		border: "1px solid rgba(0, 0, 0, 0.12)",
		boxShadow: "0 3px 5px rgba(0, 0, 0, 0.2)",
		width: "max-content",
		maxWidth: "30rem",
		padding: 8,
		display: "flex",
		flexWrap: "wrap",
		gap: 4,
	},
}));

// Each list arrives as a comma separated string, and the visible budget is spent in TO, CC, BCC
// order: whatever does not fit moves to the tooltip behind the ellipsis chip.
const getGroups = (values: RecipientValues, maxVisible: number) => {
	const groups = RECIPIENT_ORDER.map((type) => ({
		type,
		items:
			values[type]
				?.split(",")
				.map((item) => item.trim())
				.filter(Boolean) ?? [],
	})).filter((group) => group.items.length > 0);

	const shownGroups: { type: RecipientType; items: string[] }[] = [];
	const hiddenGroups: { type: RecipientType; items: string[] }[] = [];
	let remaining = maxVisible;

	for (const group of groups) {
		const shown = group.items.slice(0, Math.max(0, remaining));
		const rest = group.items.slice(shown.length);
		remaining -= shown.length;

		if (shown.length > 0) {
			shownGroups.push({ type: group.type, items: shown });
		}

		if (rest.length > 0) {
			hiddenGroups.push({ type: group.type, items: rest });
		}
	}

	const hidden = hiddenGroups.reduce(
		(sum, group) => sum + group.items.length,
		0,
	);

	return { groups: shownGroups, hiddenGroups, hidden };
};

// ag-grid sorts, filters and exports on the value, not on the rendered chips.
export const getRecipientText = (
	values: RecipientValues,
	maxVisible: number,
) => {
	const { groups, hidden } = getGroups(values, maxVisible);

	if (groups.length === 0) {
		return "-";
	}

	const text = groups
		.map((group) => `${group.type}: ${group.items.join(", ")}`)
		.join(", ");

	return hidden > 0 ? `${text} …` : text;
};

const RecipientChip = ({
	type,
	item,
}: {
	type: RecipientType;
	item: string;
}) => {
	const color = RECIPIENT_TYPE_COLOR[type];

	return (
		<Chip
			label={item}
			title={`${type}: ${item}`}
			size="small"
			variant="outlined"
			style={{
				height: 20,
				color,
				borderColor: alpha(color, 0.5),
				backgroundColor: alpha(color, 0.08),
				fontSize: "0.7rem",
			}}
		/>
	);
};

export const RecipientTypeChips = ({
	values,
	maxVisible,
}: {
	values: RecipientValues;
	maxVisible: number;
}) => {
	const classes = useStyles();
	const { groups, hiddenGroups, hidden } = getGroups(values, maxVisible);

	if (groups.length === 0) {
		return <>-</>;
	}

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				flexWrap: "wrap",
				gap: 4,
				padding: "4px 0",
				width: "100%",
			}}
		>
			{groups.flatMap((group) =>
				group.items.map((item) => (
					<RecipientChip
						key={`${group.type}-${item}`}
						type={group.type}
						item={item}
					/>
				)),
			)}
			{hidden > 0 ? (
				<Tooltip
					classes={{ tooltip: classes.tooltip }}
					title={
						<>
							{hiddenGroups.flatMap((group) =>
								group.items.map((item) => (
									<RecipientChip
										key={`${group.type}-${item}`}
										type={group.type}
										item={item}
									/>
								)),
							)}
						</>
					}
				>
					<Chip
						label="…"
						size="small"
						variant="outlined"
						style={{
							height: 20,
							cursor: "default",
							fontSize: "0.7rem",
							fontWeight: 600,
						}}
					/>
				</Tooltip>
			) : null}
		</div>
	);
};

export const RecipientTypeLegend = () => (
	<div
		style={{
			display: "flex",
			alignItems: "center",
			height: "100%",
			minHeight: 32,
			gap: 12,
			padding: "0 10px",
		}}
	>
		<Typography
			variant="caption"
			color="textSecondary"
			style={{ fontSize: "0.8rem" }}
		>
			Recipient type:
		</Typography>
		{RECIPIENT_ORDER.map((type) => {
			const color = RECIPIENT_TYPE_COLOR[type];

			return (
				<div
					key={type}
					style={{ display: "flex", alignItems: "center", gap: 6 }}
				>
					<div
						style={{
							width: 12,
							height: 12,
							borderRadius: "100%",
							border: `1px solid ${alpha(color, 0.5)}`,
							backgroundColor: alpha(color, 0.25),
						}}
					/>
					<Typography
						variant="caption"
						style={{ color, fontWeight: 600, fontSize: "0.8rem" }}
					>
						{type}
					</Typography>
				</div>
			);
		})}
	</div>
);
